import type { OfflineEntity } from './db'
import { queryClient } from '../queryClient'

type CachedPage = { data?: unknown[] }

const ID_FIELD: Partial<Record<OfflineEntity, string>> = {
  stoppage: 'stoppageId',
  geoRehandle: 'geoRehandleId',
  loading: 'loadingId',
  diggingPattern: 'diggingPatternId',
}

// Note: the client caches a row with the rowVersion it read. Once an offline update syncs, the server
// has moved that row on, so the cached copy is a version behind - and the next offline edit of the
// same row was rejected as a conflict with nobody else having touched it. The sync returns the new
// rowVersion for an applied update; write it into every cached copy of the row.
export function patchCachedRowVersion(
  entity: OfflineEntity,
  entityId: number | undefined,
  rowVersion: string | undefined,
): void {
  const idField = ID_FIELD[entity]
  if (!idField || !entityId || !rowVersion) return

  const matches = (row: unknown): boolean =>
    !!row && typeof row === 'object' && (row as Record<string, unknown>)[idField] === entityId

  for (const [key, cached] of queryClient.getQueriesData<CachedPage>({ queryKey: ['dataTable'] })) {
    const rows = cached?.data
    if (!Array.isArray(rows) || !rows.some(matches)) continue

    queryClient.setQueryData(key, {
      ...cached,
      data: rows.map(row => matches(row)
        ? Object.assign(Object.create(Object.getPrototypeOf(row)), row, { rowVersion })
        : row),
    })
  }
}

// Note: when a create syncs, the server gives the row its real id - but the cached copy kept the
// negative temp id it was created with. The queue's own references are remapped (remapQueuedEntityId);
// the cache was not, so the row on screen still claimed an id the server has never heard of. Editing
// it again queued an update against that id, and because the create had already been drained there was
// nothing left for the queue to merge it into: the server answered NotFound and the crew's typed value
// vanished. Rewrite the id in every cached copy so a row that has synced looks synced.
export function patchCachedEntityId(
  entity: OfflineEntity,
  tempId: number | undefined,
  serverId: number | undefined,
): void {
  const idField = ID_FIELD[entity]
  // Note: only ever a temp id, which is negative by construction. A positive id is already the real one.
  if (!idField || !tempId || !serverId || tempId >= 0) return

  const matches = (row: unknown): boolean =>
    !!row && typeof row === 'object' && (row as Record<string, unknown>)[idField] === tempId

  for (const [key, cached] of queryClient.getQueriesData<CachedPage>({ queryKey: ['dataTable'] })) {
    const rows = cached?.data
    if (!Array.isArray(rows) || !rows.some(matches)) continue

    queryClient.setQueryData(key, {
      ...cached,
      data: rows.map(row => (matches(row) ? { ...(row as object), [idField]: serverId } : row)),
    })
  }
}
